import { useQuery } from "@tanstack/react-query";
import { api } from "../api/endpoints";
import { ApiError } from "../api/client";
import type { CrossBatchListParams } from "../api/types";
import { getContractReportSnapshot, mergeContractReportSnapshotSummaries } from "../features/contract-report-snapshots/contractReportSnapshots";

export const queryKeys = {
  overview: ["overview"] as const,
  features: ["features"] as const,
  projects: ["projects"] as const,
  project: (projectId: string) => ["projects", projectId] as const,
  projectVersions: (projectId: string) => ["projects", projectId, "versions"] as const,
  projectVersion: (projectId: string, versionId: string) => ["projects", projectId, "versions", versionId] as const,
  projectQuestions: (projectId: string, query = "") => ["projects", projectId, "questions", query] as const,
  projectQuestion: (projectId: string, questionId: string) => ["projects", projectId, "questions", questionId] as const,
  projectTaxonomy: (projectId: string, query = "") => ["projects", projectId, "taxonomy", query] as const,
  projectQuestionImports: (projectId: string) => ["projects", projectId, "question-imports"] as const,
  projectQuestionImport: (projectId: string, jobId: string) => ["projects", projectId, "question-imports", jobId] as const,
  projectCoverage: (projectId: string) => ["projects", projectId, "coverage"] as const,
  testPlans: (projectId: string) => ["projects", projectId, "test-plans"] as const,
  gatePolicies: (projectId: string) => ["gate-policies", projectId] as const,
  notificationChannels: (projectId: string) => ["notification-channels", projectId] as const,
  notificationEvents: (projectId: string) => ["notification-events", projectId] as const,
  config: ["config"] as const,
  systemStatus: ["system-status"] as const,
  suites: ["suites"] as const,
  suitesByProject: (projectId: string) => ["suites", "project", projectId] as const,
  suite: (suiteId: string) => ["suites", suiteId] as const,
  projectSuite: (projectId: string, suiteId: string) => ["suites", "project", projectId, suiteId] as const,
  questions: (query: string) => ["questions", query] as const,
  questionTaxonomy: ["question-taxonomy"] as const,
  questionImports: ["question-imports"] as const,
  questionImport: (jobId: string) => ["question-imports", jobId] as const,
  runs: ["runs"] as const,
  runsByProject: (projectId = "") => ["runs", "project", projectId] as const,
  run: (runId: string) => ["runs", runId] as const,
  projectRun: (projectId: string, runId: string) => ["runs", "project", projectId, runId] as const,
  logs: (runId: string, query: string) => ["logs", runId, query] as const,
  connections: (scope = "", projectId = "") => ["connections", scope, projectId] as const,
  connectionCapabilityChecks: (connectionId: string) => ["connections", connectionId, "capability-checks"] as const,
  evaluationProfiles: (scope = "", projectId = "") => ["evaluation-profiles", scope, projectId] as const,
  projectPrompts: (projectId: string, category = "") => ["projects", projectId, "prompts", category] as const,
  projectTargets: (projectId: string) => ["projects", projectId, "targets"] as const,
  projectEnvBindings: (projectId: string) => ["projects", projectId, "env-bindings"] as const,
  productionSyncPolicies: (projectId: string) => ["projects", projectId, "production-sync-policies"] as const,
  productionSyncJobs: (projectId: string) => ["projects", projectId, "production-sync-jobs"] as const,
  productionSyncJob: (projectId: string, jobId: string) => ["projects", projectId, "production-sync-jobs", jobId] as const,
  productionSampleMetrics: (projectId: string) => ["projects", projectId, "production-sample-metrics"] as const,
  productionCandidates: (projectId: string, query = "") => ["projects", projectId, "production-candidates", query] as const,
  productionCandidate: (projectId: string, candidateId: string) => ["projects", projectId, "production-candidates", candidateId] as const,
  crossSources: ["cross-evaluations", "sources"] as const,
  crossPrompt: ["cross-evaluations", "prompt"] as const,
  crossBatches: (params: CrossBatchListParams = {}) => ["cross-evaluations", "batches", params] as const,
  crossBatch: (batchId: string) => ["cross-evaluations", "batches", batchId] as const,
  crossEvents: (batchId: string) => ["cross-evaluations", "batches", batchId, "events"] as const,
  crossReport: (batchId: string) => ["cross-evaluations", "batches", batchId, "report"] as const,
};

export function useOverviewQuery() {
  return useQuery({ queryKey: queryKeys.overview, queryFn: api.overview, staleTime: 15_000, refetchInterval: 30_000 });
}

export function useFeaturesQuery() {
  return useQuery({ queryKey: queryKeys.features, queryFn: api.features, staleTime: 60_000 });
}

const ACTIVE_CROSS_STATUSES = new Set(["queued", "running", "evaluating", "stopping"]);

export function useCrossSourcesQuery() {
  return useQuery({ queryKey: queryKeys.crossSources, queryFn: api.crossSources, staleTime: 10_000, retry: 1 });
}

export function useCrossPromptQuery() {
  return useQuery({ queryKey: queryKeys.crossPrompt, queryFn: api.crossPrompt, staleTime: 10_000, retry: 1 });
}

export function useCrossBatchesQuery(params: CrossBatchListParams = {}) {
  return useQuery({
    queryKey: queryKeys.crossBatches(params),
    queryFn: () => api.crossBatches(params),
    refetchInterval: (query) => query.state.data?.items.some((batch) => ACTIVE_CROSS_STATUSES.has(batch.status)) ? 2_000 : false,
    placeholderData: (previous) => previous,
  });
}

export function useCrossBatchQuery(batchId: string) {
  return useQuery({
    queryKey: queryKeys.crossBatch(batchId),
    queryFn: () => api.crossBatch(batchId),
    enabled: Boolean(batchId),
    refetchInterval: (query) => ACTIVE_CROSS_STATUSES.has(query.state.data?.status || "") ? 2_000 : false,
  });
}

export function useCrossEventsQuery(batchId: string) {
  return useQuery({ queryKey: queryKeys.crossEvents(batchId), queryFn: () => api.crossEvents(batchId), enabled: Boolean(batchId), refetchInterval: 3_000 });
}

export function useCrossReportQuery(batchId: string) {
  return useQuery({
    queryKey: queryKeys.crossReport(batchId),
    queryFn: () => api.crossReport(batchId),
    enabled: Boolean(batchId),
    refetchInterval: (query) => ACTIVE_CROSS_STATUSES.has(query.state.data?.batch.status || "") ? 2_000 : false,
  });
}

export function useProjectsQuery() {
  return useQuery({ queryKey: queryKeys.projects, queryFn: () => api.projects(), staleTime: 15_000 });
}

export function useProjectQuery(projectId: string) {
  return useQuery({ queryKey: queryKeys.project(projectId), queryFn: () => api.project(projectId), enabled: Boolean(projectId), staleTime: 10_000 });
}

export function useProjectVersionsQuery(projectId: string) {
  return useQuery({ queryKey: queryKeys.projectVersions(projectId), queryFn: () => api.projectVersions(projectId), enabled: Boolean(projectId) });
}

export function useProjectVersionQuery(projectId: string, versionId: string) {
  return useQuery({ queryKey: queryKeys.projectVersion(projectId, versionId), queryFn: () => api.projectVersion(projectId, versionId), enabled: Boolean(projectId && versionId) });
}

export function useProjectQuestionsQuery(projectId: string, params = new URLSearchParams()) {
  const query = params.toString();
  return useQuery({
    queryKey: queryKeys.projectQuestions(projectId, query),
    queryFn: () => api.projectQuestions(projectId, new URLSearchParams(query)),
    enabled: Boolean(projectId),
    staleTime: 5_000,
    placeholderData: (previous) => previous,
  });
}

export function useProjectTaxonomyQuery(projectId: string, params = new URLSearchParams()) {
  const query = params.toString();
  return useQuery({
    queryKey: queryKeys.projectTaxonomy(projectId, query),
    queryFn: () => api.projectTaxonomy(projectId, new URLSearchParams(query)),
    enabled: Boolean(projectId),
    staleTime: 5_000,
    placeholderData: (previous) => previous,
  });
}

export function useProjectQuestionImportsQuery(projectId: string) {
  return useQuery({ queryKey: queryKeys.projectQuestionImports(projectId), queryFn: () => api.projectQuestionImports(projectId), enabled: Boolean(projectId), staleTime: 5_000 });
}

export function useProjectCoverageQuery(projectId: string) {
  return useQuery({ queryKey: queryKeys.projectCoverage(projectId), queryFn: () => api.projectCoverage(projectId), enabled: Boolean(projectId) });
}

export function useTestPlansQuery(projectId: string) {
  return useQuery({ queryKey: queryKeys.testPlans(projectId), queryFn: () => api.testPlans(projectId), enabled: Boolean(projectId) });
}

export function useGatePoliciesQuery(projectId: string) {
  return useQuery({ queryKey: queryKeys.gatePolicies(projectId), queryFn: () => api.gatePolicies(projectId), enabled: Boolean(projectId) });
}

export function useNotificationChannelsQuery(projectId: string) {
  return useQuery({ queryKey: queryKeys.notificationChannels(projectId), queryFn: () => api.notificationChannels(projectId), enabled: Boolean(projectId) });
}

export function useNotificationEventsQuery(projectId: string) {
  return useQuery({ queryKey: queryKeys.notificationEvents(projectId), queryFn: () => api.notificationEvents(projectId), enabled: Boolean(projectId), refetchInterval: 15_000 });
}

const ACTIVE_RUN_STATUSES = new Set(["queued", "validating", "running", "stopping", "completed_pending_sync"]);

export function useConfigQuery() {
  return useQuery({ queryKey: queryKeys.config, queryFn: api.config, staleTime: 30_000 });
}

export function useSystemStatusQuery() {
  return useQuery({ queryKey: queryKeys.systemStatus, queryFn: api.systemStatus, staleTime: 15_000, retry: 1 });
}

export function useSuitesQuery(projectId = "") {
  return useQuery({
    queryKey: projectId ? queryKeys.suitesByProject(projectId) : queryKeys.suites,
    queryFn: () => api.suites(projectId),
    staleTime: 5_000,
  });
}

export function useSuiteQuery(suiteId: string, projectId = "") {
  return useQuery({
    queryKey: projectId ? queryKeys.projectSuite(projectId, suiteId) : queryKeys.suite(suiteId),
    queryFn: () => api.suite(suiteId, projectId),
    enabled: Boolean(suiteId),
  });
}

export function useQuestionsQuery(params: URLSearchParams) {
  const query = params.toString();
  return useQuery({
    queryKey: queryKeys.questions(query),
    queryFn: () => api.questions(new URLSearchParams(query)),
    staleTime: 5_000,
  });
}

export function useQuestionTaxonomyQuery() {
  return useQuery({ queryKey: queryKeys.questionTaxonomy, queryFn: api.questionTaxonomy, staleTime: 60_000 });
}

export function useQuestionImportsQuery() {
  return useQuery({ queryKey: queryKeys.questionImports, queryFn: api.questionImports, staleTime: 5_000 });
}

export function useQuestionImportQuery(jobId: string) {
  return useQuery({
    queryKey: queryKeys.questionImport(jobId),
    queryFn: () => api.questionImport(jobId),
    enabled: Boolean(jobId),
  });
}

export function useRunsQuery(projectId = "") {
  return useQuery({
    queryKey: projectId ? queryKeys.runsByProject(projectId) : queryKeys.runs,
    queryFn: async () => mergeContractReportSnapshotSummaries(
      projectId,
      await api.runs(projectId ? { project_id: projectId } : undefined),
    ),
    refetchInterval: (query) => query.state.data?.items.some((run) => ACTIVE_RUN_STATUSES.has(run.status ?? "")) ? 2_000 : false,
  });
}

export function useConnectionsQuery(params?: { scope?: string; project_id?: string }) {
  const scope = params?.scope ?? "";
  const projectId = params?.project_id ?? "";
  return useQuery({
    queryKey: queryKeys.connections(scope, projectId),
    queryFn: () => api.connections(params),
    staleTime: 10_000,
    retry: 1,
  });
}

export function useConnectionCapabilityChecksQuery(connectionId: string) {
  return useQuery({
    queryKey: queryKeys.connectionCapabilityChecks(connectionId),
    queryFn: () => api.connectionCapabilityChecks(connectionId),
    enabled: Boolean(connectionId),
    staleTime: 10_000,
  });
}

export function useEvaluationProfilesQuery(params?: { scope?: string; project_id?: string }) {
  const scope = params?.scope ?? "";
  const projectId = params?.project_id ?? "";
  return useQuery({
    queryKey: queryKeys.evaluationProfiles(scope, projectId),
    queryFn: () => api.evaluationProfiles(params),
    staleTime: 10_000,
    retry: 1,
  });
}

export function useProjectPromptsQuery(projectId: string, category = "") {
  return useQuery({
    queryKey: queryKeys.projectPrompts(projectId, category),
    queryFn: () => api.listProjectPrompts(projectId, category ? { category } : undefined),
    enabled: Boolean(projectId),
    staleTime: 10_000,
    retry: 1,
  });
}

export function useProjectTargetsQuery(projectId: string) {
  return useQuery({
    queryKey: queryKeys.projectTargets(projectId),
    queryFn: () => api.projectTargets(projectId),
    enabled: Boolean(projectId),
    staleTime: 10_000,
    retry: 1,
  });
}

export function useProjectEnvBindingsQuery(projectId: string) {
  return useQuery({
    queryKey: queryKeys.projectEnvBindings(projectId),
    queryFn: () => api.projectEnvBindings(projectId),
    enabled: Boolean(projectId),
    staleTime: 10_000,
    retry: 1,
  });
}

export function useProductionSyncPoliciesQuery(projectId: string) {
  return useQuery({
    queryKey: queryKeys.productionSyncPolicies(projectId),
    queryFn: () => api.productionSyncPolicies(projectId),
    enabled: Boolean(projectId),
    staleTime: 10_000,
  });
}

export function useProductionSyncJobsQuery(projectId: string) {
  return useQuery({
    queryKey: queryKeys.productionSyncJobs(projectId),
    queryFn: () => api.productionSyncJobs(projectId),
    enabled: Boolean(projectId),
    refetchInterval: (query) => query.state.data?.items.some((job) => ["queued", "running"].includes(job.status)) ? 2_000 : false,
  });
}

export function useProductionSyncJobQuery(projectId: string, jobId: string) {
  return useQuery({
    queryKey: queryKeys.productionSyncJob(projectId, jobId),
    queryFn: () => api.productionSyncJob(projectId, jobId),
    enabled: Boolean(projectId && jobId),
    refetchInterval: (query) => ["queued", "running"].includes(query.state.data?.status || "") ? 2_000 : false,
  });
}

export function useProductionSampleMetricsQuery(projectId: string) {
  return useQuery({
    queryKey: queryKeys.productionSampleMetrics(projectId),
    queryFn: () => api.productionSampleMetrics(projectId),
    enabled: Boolean(projectId),
    staleTime: 10_000,
  });
}

export function useProductionCandidatesQuery(projectId: string, params: URLSearchParams) {
  const query = params.toString();
  return useQuery({
    queryKey: queryKeys.productionCandidates(projectId, query),
    queryFn: () => api.productionCandidates(projectId, new URLSearchParams(query)),
    enabled: Boolean(projectId),
    staleTime: 5_000,
    placeholderData: (previous) => previous,
  });
}

export function useProductionCandidateQuery(projectId: string, candidateId: string) {
  return useQuery({
    queryKey: queryKeys.productionCandidate(projectId, candidateId),
    queryFn: () => api.productionCandidate(projectId, candidateId),
    enabled: Boolean(projectId && candidateId),
  });
}

export function useRunQuery(runId: string, projectId = "") {
  return useQuery({
    queryKey: projectId ? queryKeys.projectRun(projectId, runId) : queryKeys.run(runId),
    queryFn: ({ signal }) => getContractReportSnapshot(projectId, runId) ?? api.run(runId, signal, projectId),
    enabled: Boolean(runId),
    staleTime: 15_000,
    refetchInterval: (query) => {
      const run = query.state.data;
      const status = String(run?.status ?? "").toLowerCase();
      const phase = String(run?.progress?.phase ?? "").toLowerCase();
      return ACTIVE_RUN_STATUSES.has(status) || phase === "case_retry_running" ? 2_000 : false;
    },
    retry: (failureCount, error) => !(error instanceof ApiError && error.status === 404) && failureCount < 1,
  });
}
